import classes from "./ChangeStudioModal.module.css";
import React, { useState, useEffect } from "react";
import { SmallModal } from "../../components/UI/SmallModal/SmallModal";
import { useFetchStudiosForService } from "../../hooks/useFetchStudiosForService";
import ErrorMessage from "../../components/StudioErstellen/ErrorMessage";

export const ChangeStudioModal = ({ 
        userID, 
        showChangeStudioModal, 
        onClose, 
        onStudioSelect,
        selectedStudioId: propSelectedStudioId,
        title = "In welchem Studio wurde dein Projekt realisiert?"
    }) => {
    const [errors, setErrors] = useState({
        selectedStudioId: ""
    });
    const [selectedStudioId, setSelectedStudioId] = useState(propSelectedStudioId || null);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 742);
    const { studios, loading: studiosLoading, error: studiosError } = useFetchStudiosForService(userID);
    
    // Update local state when prop changes (when modal opens with a new value)
    useEffect(() => {
        if (propSelectedStudioId !== undefined) {
            setSelectedStudioId(propSelectedStudioId);
        }
    }, [propSelectedStudioId, showChangeStudioModal]);

    // Handle window resize to detect mobile
    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 742);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Lock body scroll when full-page modal is open on mobile
    useEffect(() => {
        if (isMobile && showChangeStudioModal) {
            const originalOverflow = document.body.style.overflow;
            document.body.style.overflow = 'hidden';
            return () => {
                document.body.style.overflow = originalOverflow;
            };
        }
    }, [isMobile, showChangeStudioModal]);

    const handleOptionChange = (studioId) => {
        // Toggle selection: if clicking the same studio, deselect it; otherwise select the new one
        if (selectedStudioId === studioId) {
            setSelectedStudioId(null);
        } else {
            setSelectedStudioId(studioId);
        }
    };
    
    const handleSave = () => {
        // Always call onStudioSelect if it exists, even if selectedStudioId is null (to allow clearing)
        if (onStudioSelect) {
            onStudioSelect(selectedStudioId);
        }
        onClose();
    };
    
    // Determine checked state based on local selectedStudioId
    const isChecked = (studioId) => {
        return selectedStudioId === studioId;
    };

    const content = (
        <div className={classes.chooseStudio}>
            <header className={classes.phoneHeader}>
                <div className={classes.phoneHeaderBackButton} onClick={onClose}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="11" height="19" viewBox="0 0 11 19" fill="none">
                        <line x1="9.60104" y1="0.601041" x2="0.602493" y2="9.59959" stroke="#1F1F1F" strokeWidth="1.7"/>
                        <line x1="0.624584" y1="8.42346" x2="9.62313" y2="18.1719" stroke="#1F1F1F" strokeWidth="1.7"/>
                    </svg>
                    <span className="mediumLarge">Zurück</span>
                </div>
            </header>
            <header className={classes.header}>
                <h2 className={`${classes.title} titleSemibold2`}>{title}</h2>
                <div className={classes.phoneHeaderTitleContainer}>
                    <h2 className={`${classes.phoneTitle} titleSemibold`}>Wo soll dein Service angeboten werden?</h2>
                    <p className="slimRegularLargeText" style={{color: "var(--dark-grey-4)"}}>Du kannst kreative Services aktuell nur zusammen mit einem Studio anbieten.</p>
                </div>
                <svg className={classes.closeButton} onClick={onClose} xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 17 17" fill="none">
                    <path fillRule="evenodd" clipRule="evenodd" d="M7.50947 8.5L0 0.990535L0.990535 0L8.5 7.50947L16.0095 0L17 0.990535L9.49054 8.5L17 16.0095L16.0095 17L8.5 9.49054L0.990535 17L0 16.0095L7.50947 8.5Z" fill="#1F1F1F"/>
                </svg>
            </header>
            {studiosLoading ? 
            <div className={classes.loading}>
                <span className="mediumLarge">Lade deine Studios...</span>
            </div> 
            : studiosError ? <div>Fehler beim Laden deiner Studios: {studiosError.message}</div> : (
            <div className={classes.studiosList}>
                {studios?.map((studio) => (
                    <div 
                        key={studio.id} 
                        className={classes.option}
                    >   
                        <div 
                            className={classes.studioInfo}
                            onClick={() => handleOptionChange(studio.id)}
                        >
                            <div className={classes.studioImage}>
                                <img src={studio.image} alt={studio.StudioName} />
                            </div>
                            <div 
                                className={classes.containerLabels}
                            >
                                <label
                                    style={{ marginBottom: "6px" }}
                                    className={`${classes.titleTruncate} titleSemibold3`}
                                >
                                    {studio.StudioName}
                                </label>
                                <label className={`${classes.label} slimRegularSmallText`}>{studio.displayLocation}</label>
                            </div>
                        </div>
                        <input
                            className={classes.inputs}
                            type="checkbox"
                            id={studio.id}
                            name="studioType"
                            value={studio.id}
                            checked={isChecked(studio.id)}
                            onChange={() => handleOptionChange(studio.id)}
                        />
                    </div>
                ))}
                {errors.selectedStudioId && <ErrorMessage>{errors.selectedStudioId}</ErrorMessage>}
            </div>
            )}
            <button 
                style={{backgroundColor: selectedStudioId ? "var(--dark-grey)" : "var(--blue-grey-ultralight)", 
                color: selectedStudioId ? "var(--white)" : "var(--grey-1)"}} 
                className={`${classes.saveButton} mediumLarge`} 
                onClick={handleSave}>
                    Speichern
            </button>

            <footer className={classes.phoneFooter}>
                <button 
                    style={{backgroundColor: selectedStudioId ? "var(--dark-grey)" : "var(--blue-grey-ultralight)", 
                        color: selectedStudioId ? "var(--white)" : "var(--grey-1)"}}
                    className={`${classes.phoneSaveButton} mediumLarge`} 
                    onClick={handleSave}>
                        Speichern
                </button>
            </footer>
        </div>
    );
    
    // On mobile (under 742px), render as full-page instead of modal
    if (isMobile && showChangeStudioModal) {
        return (
            <div className={classes.fullPageContainer}>
                {content}
            </div>
        );
    }
    
    return (
        <SmallModal changeStudioModal={true} open={showChangeStudioModal} onBackdropClick={onClose}>
            {content}
        </SmallModal>
    );
};
